/**
 * canWithdraw.js — WHEN A PATIENT MAY TAKE A CASE BACK. v242.152.0
 *
 * ═══ WHY THIS IS A MODULE AND NOT AN INLINE CONDITION ═══
 * "I can't withdraw a case as long it has not been scheduled." The capability
 * has existed since API v138.1.0 — `POST /cases/<id>/revoke-share` clears the
 * share code, both doctor fields and the appointment request, deletes the
 * appointment rows, notifies the doctor and returns the case to
 * `ready_to_share`. `utils/withdrawCase.js` has called it from two screens since
 * v242.106.0.
 *
 * ★★★ THE CONTROL WAS SIMPLY NESTED INSIDE APPOINTMENT UI. On Case Home it lived
 *     inside `<AppointmentActions>`, rendered only when
 *     `formattedOriginalAppointment && …date`. A case that a doctor has CLAIMED
 *     but not yet scheduled has no appointment, so the row never drew and the
 *     patient had no way to take the case back — while the server would have
 *     accepted the call the whole time. THE PERMISSION AND THE AFFORDANCE WERE
 *     ANSWERING DIFFERENT QUESTIONS: "is there an appointment to cancel" is not
 *     "may this case be withdrawn".
 *
 * ★★ SO THE RULE LIVES HERE, AND BOTH SCREENS ASK IT. One owner, or the two
 *    surfaces drift and the button appears on one screen and not the other —
 *    which is exactly the state this was found in.
 *
 * ★★★ THE CLIENT IS DELIBERATELY STRICTER THAN THE SERVER, AND MAY ONLY EVER BE
 *     STRICTER. The API refuses a withdrawal only for `completed`,
 *     `in_follow_up` and `in_observation`. This adds `checked_in` — a patient
 *     standing in the office having just checked in should not be offered
 *     "cancel & withdraw" — plus the states that have no doctor to withdraw
 *     from. Offering an action the server will refuse is a broken button;
 *     withholding one it would allow is a product decision. `check-withdraw-
 *     affordance.mjs` asserts the containment in that direction only.
 */

/**
 * What the API refuses, verbatim from `revoke_share_code` (api/index.py):
 *     if old_status in ('completed', 'in_follow_up', 'in_observation'):
 *         return 'Cannot withdraw a case that has been …'
 * Mirrored here so the gate can compare the two lists. If the server's list
 * changes, this one moves with it.
 */
export const SERVER_BLOCKS_WITHDRAW = Object.freeze([
  'completed',
  'in_follow_up',
  'in_observation',
])

/** Everything the client refuses. Must be a SUPERSET of the server's list. */
export const CLIENT_BLOCKS_WITHDRAW = Object.freeze([
  ...SERVER_BLOCKS_WITHDRAW,
  'checked_in',      // already at the visit — cancelling is a conversation, not a button
  'archived',        // ended; Reactivate is its path back
  'draft',           // never shared, nothing to withdraw from
  'ready_to_share',  // already withdrawn / not yet shared
])

/**
 * True when this case is attached to a doctor in a way the patient can undo.
 *
 * ★ Attachment is checked in BOTH namings. The store carries snake_case from the
 *   API and camelCase from its own merge, and a case that arrived by one path
 *   and was updated by the other would otherwise read as unattached — the same
 *   class of miss that hid the medication ledger id under `ledgerId`.
 */
export function canWithdrawCase(caseItem) {
  if (!caseItem) return false
  const status = String(caseItem.status || '')
  if (CLIENT_BLOCKS_WITHDRAW.includes(status)) return false
  return !!(
    caseItem.doctor_id || caseItem.doctorId ||
    caseItem.target_doctor_id || caseItem.targetDoctorId ||
    caseItem.share_code || caseItem.shareCode ||
    caseItem.isShared
  )
}

export default canWithdrawCase
